import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon, type IconName } from '@/components/Icon';
import { RequireRole } from '@/components/RequireRole';
import { useHasRole } from '@/hooks/useHasRole';
import {
  Banner,
  CodeChip,
  EmptyState,
  ErrorPage,
  LoadingPage,
  SectionTitle,
  SparkRuns,
  Stat,
  StatusBadge,
  Switch,
  TestModeChip,
  TriggerChip,
  WorkflowLabelChips,
} from '@/components/primitives';
import { RunsTable } from '@/components/RunsTable';
import { formatCount, hasItemCounts } from '@/lib/itemCounts';
import { startMode } from '@/lib/runMode';
import { STEP_KIND_ICON } from '@/lib/stepIcons';
import { AlertFeedRow } from '@/components/alerts/AlertFeedRow';
import { RuleCard } from '@/components/alerts/RuleCard';
import { RuleEditor } from '@/components/alerts/RuleEditor';
import { useAlerts, useChannels, useRules, useWorkflow, useWorkflowRuns, keys } from '@/hooks/useSturmData';
import { notificationsApi, workflowsApi } from '@/lib/apiClient';
import type { NotificationRule, RunStatus, WorkflowDetail, WorkflowStep } from '@/types/sturm';

type Tab = 'runs' | 'steps' | 'settings' | 'alerts';

const TABS: { id: Tab; label: string; icon: IconName }[] = [
  { id: 'runs', label: 'Runs', icon: 'history' },
  { id: 'steps', label: 'Steps', icon: 'flow' },
  { id: 'settings', label: 'Settings', icon: 'settings' },
  { id: 'alerts', label: 'Notifications', icon: 'bell' },
];

const STATUS_FILTERS: (RunStatus | 'all')[] = [
  'all', 'running', 'failed', 'completed', 'timedout', 'cancelled',
];

export function WorkflowDetailPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = (searchParams.get('tab') as Tab | null) ?? 'runs';
  const status = searchParams.get('status') ?? 'all';

  const { data: workflow, isLoading, isError, error } = useWorkflow(slug);
  const { data: runs } = useWorkflowRuns(slug, status === 'all' ? {} : { status });
  const { data: alerts = [] } = useAlerts();
  const { data: rules = [] } = useRules();

  const setTab = (next: Tab) => setSearchParams({ tab: next });

  const pause = useMutation({
    mutationFn: () => (workflow?.isPaused ? workflowsApi.resume(slug) : workflowsApi.pause(slug)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.workflow(slug) }),
  });

  if (isLoading) {
    return <LoadingPage label="Loading workflow…" />;
  }

  if (isError || !workflow) {
    return (
      <ErrorPage
        title="Could not load this workflow"
        message={error instanceof Error ? error.message : `No workflow is registered as "${slug}".`}
      />
    );
  }

  const workflowAlerts = alerts.filter((a) => a.workflowSlug === slug);
  const workflowRules = rules.filter((r) => r.scope.includes('*') || r.scope.includes(slug));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="row gap-10">
            <div className="page-title">{workflow.name}</div>
            <StatusBadge status={workflow.isDisabled ? 'disabled' : workflow.isPaused ? 'paused' : workflow.health} />
            {workflow.isTestMode ? <TestModeChip /> : null}
            <WorkflowLabelChips labels={workflow.labels} />
          </div>
          <div className="page-sub">{workflow.description}</div>
          <div className="row gap-12 mt-12" style={{ flexWrap: 'wrap' }}>
            <TriggerChip trigger={workflow.trigger} />
            <CodeChip icon="layers">{workflow.queue}</CodeChip>
            <CodeChip icon="apps">{workflow.app}</CodeChip>
            {workflow.owner ? <CodeChip icon="users">{workflow.owner}</CodeChip> : null}
            <CodeChip icon="tag">{workflow.version}</CodeChip>
          </div>
        </div>
        <div className="page-actions">
          {workflow.isDisabled ? null : (
            <RequireRole min="manager">
              <button className="btn btn-secondary" onClick={() => pause.mutate()} disabled={pause.isPending}>
                <Icon name={workflow.isPaused ? 'play' : 'pause'} size={13} />
                {workflow.isPaused ? ' Resume' : ' Pause'}
              </button>
            </RequireRole>
          )}
          <button className="btn btn-secondary" onClick={() => setTab('alerts')}>
            <Icon name="bell" size={14} /> Notify me
          </button>
          {workflow.isDisabled ? null : (
            <RequireRole min="manager">
              <StartRunButton workflow={workflow} />
            </RequireRole>
          )}
        </div>
      </div>

      {workflow.isDisabled ? (
        <div className="mb-20">
          <Banner tone="warn">
            This workflow is disabled: it is hidden from the workflow list, never scheduled, and cannot
            be started, retried or re-run. An admin can enable it under its Settings tab or in{' '}
            <a
              href="/settings"
              onClick={(e) => {
                e.preventDefault();
                navigate('/settings');
              }}
            >
              Settings → Disabled workflows
            </a>
            .
          </Banner>
        </div>
      ) : null}

      <div className="tabs">
        {TABS.map((t) => (
          <div key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            <Icon name={t.icon} size={14} /> {t.label}
            {t.id === 'runs' ? <span className="count">{workflow.runs24h.toLocaleString()}</span> : null}
            {t.id === 'steps' ? <span className="count">{workflow.steps.length}</span> : null}
            {t.id === 'alerts' ? <span className="count">{workflowRules.length}</span> : null}
          </div>
        ))}
      </div>

      {tab === 'runs' ? (
        <>
          <div className="grid-4 mb-20">
            <Stat
              icon="play"
              label="Running"
              value={workflow.running.toLocaleString()}
              meta={`concurrency limit ${workflow.concurrency}`}
            />
            <Stat
              icon="check"
              label="Success · 24h"
              value={`${workflow.successRate}%`}
              meta={`${workflow.runs24h.toLocaleString()} runs · ${workflow.failed24h} failed`}
            />
            <Stat
              icon="clock"
              label="Duration"
              value={<span className="mono">{workflow.p50 ?? '—'}</span>}
              meta={`p95 ${workflow.p95 ?? '—'} · timeout ${workflow.timeout}`}
            />
            <Stat
              icon="refresh"
              label="Retries · 24h"
              value={workflow.retries24h}
              meta="absorbed before failing"
            />
          </div>

          <div className="card mb-20">
            <div className="card-pad">
              <div className="row-between mb-12">
                <SectionTitle count="newest right">Last 40 runs</SectionTitle>
                <div className="row gap-12 tiny muted">
                  <Legend colour="#b9e0cb" label="completed" />
                  <Legend colour="var(--error)" label="failed" />
                  <Legend colour="var(--warn)" label="timed out" />
                  <Legend colour="var(--accent)" label="running" />
                </div>
              </div>
              <SparkRuns statuses={workflow.sparkline} height={54} />
            </div>
          </div>

          <div className="filterbar">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                className={`pick-btn ${status === s ? 'is-active' : ''}`}
                style={{ height: 32, padding: '0 12px' }}
                onClick={() => setSearchParams(s === 'all' ? { tab } : { tab, status: s })}
              >
                <span className="pick-title" style={{ fontSize: 12.5 }}>
                  {s === 'all' ? 'All runs' : s === 'timedout' ? 'Timed out' : s[0].toUpperCase() + s.slice(1)}
                </span>
              </button>
            ))}
            <div style={{ marginLeft: 'auto' }} className="row gap-8">
              <a className="btn btn-sm btn-secondary" href={`/api/workflows/${slug}/runs/export`}>
                <Icon name="download" size={12} /> Export
              </a>
            </div>
          </div>

          <RunsTable
            runs={runs?.items ?? []}
            showWorkflow={false}
            onOpen={(run) => navigate(`/workflows/${slug}/runs/${run.id}`)}
          />
        </>
      ) : null}

      {tab === 'steps' ? <StepsTab workflow={workflow} /> : null}
      {tab === 'settings' ? <SettingsTab workflow={workflow} /> : null}

      {tab === 'alerts' ? (
        <NotificationsTab
          slug={slug}
          rules={workflowRules}
          alerts={workflowAlerts}
          workflowName={workflow.name}
        />
      ) : null}
    </div>
  );
}

function Legend({ colour, label }: { colour: string; label: string }) {
  return (
    <span className="row gap-5">
      <i style={{ width: 8, height: 8, borderRadius: 2, background: colour }} />
      {label}
    </span>
  );
}

function StartRunButton({ workflow }: { workflow: WorkflowDetail }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button className="btn btn-accent" onClick={() => setOpen(true)}>
        <Icon name="play" size={12} /> Start run
      </button>
      {open ? <StartRunDialog workflow={workflow} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function StartRunDialog({ workflow, onClose }: { workflow: WorkflowDetail; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [requested, setRequested] = useState(false);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const mode = startMode(workflow.isTestMode, requested);

  const start = useMutation({
    mutationFn: () => workflowsApi.start(workflow.slug, {}, idempotencyKey, mode.testMode),
    onSuccess: (run) => {
      void queryClient.invalidateQueries({ queryKey: keys.workflow(workflow.slug) });
      void queryClient.invalidateQueries({ queryKey: ['runs'] });
      navigate(`/workflows/${workflow.slug}/runs/${run.id}`);
    },
  });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="modal-hd">
          <div className="modal-title">Start {workflow.name}</div>
          <div className="modal-sub">Runs once now on {workflow.queue}, outside its trigger.</div>
        </div>
        <div className="modal-body">
          <div className="field">
            <label className="field-label">Mode</label>
            <div className="row gap-8" style={{ height: 36 }}>
              <Switch
                on={mode.testMode}
                onChange={setRequested}
                label="Test mode"
                disabled={mode.locked || start.isPending}
                small
              />
              <span style={{ fontSize: 13 }}>{mode.testMode ? 'Test run' : 'Live run'}</span>
            </div>
            <div className="field-help">
              A test run reads every source and checks every credential, but creates, changes and
              deletes nothing. Its event log lists what a live run would have done.
            </div>
          </div>
          {mode.locked ? (
            <Banner>
              This workflow is in test mode, so every run is a test run. An admin can switch it live
              under Settings.
            </Banner>
          ) : null}
          {!mode.testMode ? <Banner tone="warn">This run writes to the target systems.</Banner> : null}
          {start.isError ? (
            <Banner tone="warn">
              {start.error instanceof Error ? start.error.message : 'The run could not be started.'}
            </Banner>
          ) : null}
        </div>
        <div className="modal-foot">
          <button className="btn btn-secondary" onClick={onClose} disabled={start.isPending}>
            Cancel
          </button>
          <button className="btn btn-accent" onClick={() => start.mutate()} disabled={start.isPending}>
            <Icon name="play" size={12} /> {mode.testMode ? 'Start test run' : 'Start live run'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RunModeCard({ workflow }: { workflow: WorkflowDetail }) {
  const canEdit = useHasRole('admin');
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const save = useMutation({
    mutationFn: (isTestMode: boolean) => workflowsApi.updateSettings(workflow.slug, { isTestMode }),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: keys.workflows });
    },
  });

  return (
    <div className="card">
      <div className="card-hd">
        <Icon name="eye" size={15} />
        <div className="flex1">
          <div className="card-hd-title">Run mode</div>
          <div className="card-hd-sub">Whether runs may write to the target systems.</div>
        </div>
      </div>
      <div className="card-pad stack gap-14">
        <div className="row gap-8">
          <Switch
            on={workflow.isTestMode}
            onChange={(next) => (next ? save.mutate(true) : setConfirming(true))}
            label="Test mode"
            disabled={!canEdit || save.isPending || confirming}
          />
          <span style={{ fontSize: 13 }}>
            {workflow.isTestMode
              ? 'Test mode: runs read and check credentials, nothing is written'
              : 'Live: runs create, update and delete data'}
          </span>
        </div>
        {confirming ? (
          <div className="row gap-8">
            <button className="btn btn-sm btn-danger" onClick={() => save.mutate(false)} disabled={save.isPending}>
              Confirm, runs will write
            </button>
            <button className="btn btn-sm btn-secondary" onClick={() => setConfirming(false)} disabled={save.isPending}>
              Cancel
            </button>
          </div>
        ) : null}
        {save.isError ? (
          <Banner tone="warn">
            {save.error instanceof Error ? save.error.message : 'The run mode could not be changed.'}
          </Banner>
        ) : null}
        <div className="field-help">
          New workflows start in test mode until an admin switches them live. Scheduled and manual
          runs follow this switch; a live workflow can still start a one-off test run.
        </div>
      </div>
    </div>
  );
}

function StepsTab({ workflow }: { workflow: WorkflowDetail }) {
  const [open, setOpen] = useState<string | null>(null);
  const showItems = hasItemCounts(workflow.steps);

  return (
    <div className="grid-2-1">
      <div>
        <SectionTitle count={`${workflow.version} · ${workflow.steps.length} steps`}>Definition</SectionTitle>
        {workflow.steps.length === 0 ? (
          <div className="card">
            <EmptyState
              title="No steps declared"
              sub="Steps are derived from the activity calls the workflow body makes."
            />
          </div>
        ) : (
          <div className="lane">
            {workflow.steps.map((step) => (
              <StepRow
                key={step.name}
                step={step}
                workflow={workflow}
                showItems={showItems}
                isOpen={open === step.name}
                onToggle={() => setOpen(open === step.name ? null : step.name)}
              />
            ))}
          </div>
        )}
      </div>
      <div className="stack gap-16">
        <div className="card">
          <div className="card-hd">
            <div className="card-hd-title">Where time goes</div>
          </div>
          <div className="card-pad stack gap-10">
            {workflow.steps.map((step, index) => (
              <div key={step.name}>
                <div className="row-between tiny" style={{ marginBottom: 4 }}>
                  <span className="mono" style={{ color: 'var(--ink-700)' }}>
                    {step.name}
                  </span>
                  <span className="muted mono">{step.averageDuration ?? '—'}</span>
                </div>
                <div className="dbar">
                  <i
                    className={step.failureRate > 3 ? 'd-failed' : 'd-completed'}
                    style={{ width: `${[46, 22, 14, 9, 6, 3][index] ?? 3}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <Banner>
          Step definitions come from the assembly at startup. Changing a step&apos;s shape starts a new
          version — in-flight runs finish on <span className="mono">{workflow.version}</span>.
        </Banner>
      </div>
    </div>
  );
}

function StepRow({
  step,
  workflow,
  showItems,
  isOpen,
  onToggle,
}: {
  step: WorkflowStep;
  workflow: WorkflowDetail;
  showItems: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const unhealthy = step.failureRate > 3;

  return (
    <div className="lane-row">
      <div className="lane-rail">
        <div className={`lane-node ${unhealthy ? 'st-failed' : 'st-completed'}`}>
          <Icon name={unhealthy ? 'alert' : 'check'} size={11} stroke={2.5} />
        </div>
      </div>
      <div className="lane-body">
        <div className={`lane-card ${isOpen ? 'is-open' : ''}`} onClick={onToggle}>
          <div className="flex1" style={{ minWidth: 0 }}>
            <div className="row gap-8">
              <span className="lane-name">{step.name}</span>
              <span className="wf-kind">
                <Icon name={STEP_KIND_ICON[step.kind]} size={10} />
                {step.kind}
              </span>
            </div>
            <div className="tiny muted mt-4">{describeStep(step, workflow.queue)}</div>
          </div>
          <div className="lane-meta">
            {showItems ? <LaneStat label="Items 7d" value={formatCount(step.items)} /> : null}
            <LaneStat label="Avg" value={step.averageDuration ?? '—'} />
            <LaneStat label="Fail rate" value={`${step.failureRate}%`} bad={unhealthy} />
            <LaneStat label="Timeout" value={step.timeout} />
            <Icon name={isOpen ? 'chevronDown' : 'chevron'} size={14} style={{ color: 'var(--ink-300)' }} />
          </div>
        </div>
        {isOpen ? (
          <div className="lane-detail">
            <div className="kv">
              <KvRow k="Handler" v={`${workflow.app} · ${step.name}`} />
              <KvRow
                k="Retry policy"
                v={`${workflow.retry.maxAttempts}× · ${workflow.retry.initialInterval} ×${workflow.retry.backoffMultiplier} → ${workflow.retry.maxInterval}`}
              />
              <KvRow k="Heartbeat" v={step.kind === 'activity' ? '30s' : '—'} />
              <KvRow k="Idempotency" v="key + attempt" />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function describeStep(step: WorkflowStep, queue: string): string {
  switch (step.kind) {
    case 'signal':
      return 'Waits for an external signal or times out.';
    case 'timer':
      return 'Durable timer — survives worker restarts.';
    case 'child':
      return 'Fans out into a child workflow per item.';
    default:
      return `Runs on ${queue}, timeout ${step.timeout}.`;
  }
}

function LaneStat({ label, value, bad }: { label: string; value: string; bad?: boolean }) {
  return (
    <div className="lane-stat">
      <span className="lane-stat-l">{label}</span>
      <span className={`lane-stat-v ${bad ? 'bad' : ''}`}>{value}</span>
    </div>
  );
}

export function KvRow({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="kv-row">
      <span className="kv-k">{k}</span>
      <span className="kv-v">{v}</span>
    </div>
  );
}

function SettingsTab({ workflow }: { workflow: WorkflowDetail }) {
  const canEdit = useHasRole('admin');

  return (
    <div className="grid-2">
      <RunModeCard workflow={workflow} />
      <AvailabilityCard workflow={workflow} />

      <div className="card">
        <div className="card-hd">
          <Icon name="server" size={15} />
          <div className="flex1">
            <div className="card-hd-title">Execution</div>
            <div className="card-hd-sub">Where and how many at a time.</div>
          </div>
        </div>
        <div className="card-pad stack gap-14">
          <div className="grid-2">
            <Field label="Task queue" value={workflow.queue} mono readOnly />
            <Field label="Workers" value={`${workflow.workers} connected`} mono readOnly />
          </div>
          <div className="grid-2">
            <Field
              label="Max concurrent runs"
              value={String(workflow.concurrency)}
              mono
              readOnly={!canEdit}
              help="Extra starts queue rather than fail."
            />
            <Field
              label="Execution timeout"
              value={workflow.timeout}
              mono
              readOnly={!canEdit}
              help="Whole run, including waits."
            />
          </div>
          <div className="field">
            <label className="field-label">Trigger</label>
            <div className="row gap-8">
              <input className="input mono" defaultValue={workflow.trigger.detail ?? workflow.trigger.kind} readOnly={!canEdit} />
            </div>
            <div className="field-help">{workflow.trigger.description}</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-hd">
          <Icon name="refresh" size={15} />
          <div className="flex1">
            <div className="card-hd-title">Retry policy</div>
            <div className="card-hd-sub">Default for every step unless the step overrides it.</div>
          </div>
        </div>
        <div className="card-pad stack gap-14">
          <div className="grid-2">
            <Field label="Max attempts" value={String(workflow.retry.maxAttempts)} mono readOnly={!canEdit} />
            <Field label="Initial interval" value={workflow.retry.initialInterval} mono readOnly={!canEdit} />
          </div>
          <div className="grid-2">
            <Field label="Backoff multiplier" value={String(workflow.retry.backoffMultiplier)} mono readOnly={!canEdit} />
            <Field label="Max interval" value={workflow.retry.maxInterval} mono readOnly={!canEdit} />
          </div>
          <div className="field">
            <label className="field-label">Never retry these errors</label>
            <div className="row gap-6" style={{ flexWrap: 'wrap' }}>
              {workflow.retry.nonRetryableErrors.length > 0 ? (
                workflow.retry.nonRetryableErrors.map((e) => (
                  <span key={e} className="chan">
                    <Icon name="close" size={11} />
                    {e}
                  </span>
                ))
              ) : (
                <span className="tiny muted">None — every error retries to the budget.</span>
              )}
            </div>
            <div className="field-help">
              A non-retryable error fails the run immediately and notifies right away.
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-hd">
          <Icon name="tag" size={15} />
          <div className="flex1">
            <div className="card-hd-title">Versioning &amp; rollout</div>
            <div className="card-hd-sub">In-flight runs keep the version they started on.</div>
          </div>
        </div>
        <div className="card-pad kv">
          <KvRow k="Current" v={workflow.version} />
          <KvRow k="In flight" v={`${workflow.version} (${workflow.running})`} />
          <KvRow k="Rollout" v="New runs on newest, drain old" />
        </div>
      </div>

      <div className="card">
        <div className="card-hd">
          <Icon name="folder" size={15} />
          <div className="flex1">
            <div className="card-hd-title">History &amp; ownership</div>
            <div className="card-hd-sub">How long a run stays inspectable.</div>
          </div>
        </div>
        <div className="card-pad stack gap-14">
          <div className="grid-2">
            <Field label="Retention" value={workflow.retention} readOnly={!canEdit} />
            <Field label="Owner" value={workflow.owner ?? '—'} readOnly={!canEdit} />
          </div>
          <div className="field-help">
            Metric buckets outlive the runs they came from, so charts survive retention sweeps.
          </div>
        </div>
      </div>
    </div>
  );
}

function AvailabilityCard({ workflow }: { workflow: WorkflowDetail }) {
  const canEdit = useHasRole('admin');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const save = useMutation({
    mutationFn: (disable: boolean) =>
      disable ? workflowsApi.disable(workflow.slug) : workflowsApi.enable(workflow.slug),
    onSuccess: (_, disable) => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: keys.workflows });
      if (disable) {
        navigate('/settings');
      }
    },
  });

  return (
    <div className="card">
      <div className="card-hd">
        <Icon name="stop" size={15} />
        <div className="flex1">
          <div className="card-hd-title">Availability</div>
          <div className="card-hd-sub">Whether the workflow exists for operators and the scheduler.</div>
        </div>
      </div>
      <div className="card-pad stack gap-14">
        <div className="row gap-8">
          <StatusBadge status={workflow.isDisabled ? 'disabled' : 'healthy'} label={workflow.isDisabled ? 'Disabled' : 'Enabled'} />
          <span style={{ fontSize: 13 }}>
            {workflow.isDisabled
              ? 'Hidden from the workflow list; no schedule, no starts, retries or re-runs'
              : 'Listed, scheduled and startable'}
          </span>
        </div>
        {canEdit ? (
          workflow.isDisabled ? (
            <div className="row gap-8">
              <button className="btn btn-sm btn-accent" onClick={() => save.mutate(false)} disabled={save.isPending}>
                <Icon name="play" size={12} /> Enable workflow
              </button>
            </div>
          ) : confirming ? (
            <div className="row gap-8">
              <button className="btn btn-sm btn-danger" onClick={() => save.mutate(true)} disabled={save.isPending}>
                Confirm, disable workflow
              </button>
              <button className="btn btn-sm btn-secondary" onClick={() => setConfirming(false)} disabled={save.isPending}>
                Cancel
              </button>
            </div>
          ) : (
            <div className="row gap-8">
              <button className="btn btn-sm btn-secondary" onClick={() => setConfirming(true)}>
                <Icon name="stop" size={12} /> Disable workflow
              </button>
            </div>
          )
        ) : null}
        {save.isError ? (
          <Banner tone="warn">
            {save.error instanceof Error ? save.error.message : 'The workflow could not be changed.'}
          </Banner>
        ) : null}
        <div className="field-help">
          Pause holds new runs but keeps the workflow listed. Disable removes it from the list and the
          scheduler until an admin enables it again from Settings → Disabled workflows. Runs already in
          flight finish; queued runs wait until it is enabled.
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  readOnly,
  help,
}: {
  label: string;
  value: string;
  mono?: boolean;
  readOnly?: boolean;
  help?: string;
}) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      <input className={`input ${mono ? 'mono' : ''}`} defaultValue={value} readOnly={readOnly} />
      {help ? <div className="field-help">{help}</div> : null}
    </div>
  );
}

function NotificationsTab({
  slug,
  rules,
  alerts,
  workflowName,
}: {
  slug: string;
  rules: NotificationRule[];
  alerts: ReturnType<typeof useAlerts>['data'];
  workflowName: string;
}) {
  const queryClient = useQueryClient();
  const { data: channels = [] } = useChannels();
  const [editing, setEditing] = useState<NotificationRule | 'new' | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.rules });

  const toggle = useMutation({
    mutationFn: (rule: NotificationRule) =>
      notificationsApi.updateRule(rule.id, { isEnabled: !rule.isEnabled }),
    onSuccess: invalidate,
  });

  const save = useMutation({
    mutationFn: (draft: Partial<NotificationRule>) =>
      draft.id ? notificationsApi.updateRule(draft.id, draft) : notificationsApi.createRule(draft),
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
  });

  return (
    <>
      <div className="row-between mb-16">
        <SectionTitle count={rules.length}>Rules watching this workflow</SectionTitle>
        <RequireRole min="manager">
          <button className="btn btn-sm btn-accent" onClick={() => setEditing('new')}>
            <Icon name="plus" size={13} /> Add rule
          </button>
        </RequireRole>
      </div>

      {editing ? (
        <RuleEditor
          rule={editing === 'new' ? null : editing}
          lockedScope={slug}
          channels={channels}
          workflows={[]}
          onClose={() => setEditing(null)}
          onSave={(draft) => save.mutate(draft)}
          isSaving={save.isPending}
        />
      ) : null}

      <div className="stack gap-12 mb-24">
        {rules.length === 0 ? (
          <div className="card">
            <EmptyState
              title="No rules watch this workflow"
              sub="Add one so a failure reaches someone instead of waiting to be noticed."
            />
          </div>
        ) : (
          rules.map((rule) => (
            <div key={rule.id}>
              {rule.scope.includes('*') ? (
                <div className="tiny muted mb-8 row gap-6">
                  <Icon name="matrix" size={12} /> Inherited from a global rule
                </div>
              ) : null}
              <RuleCard
                rule={rule}
                channels={channels}
                compact
                onToggle={() => toggle.mutate(rule)}
                onEdit={() => setEditing(rule)}
              />
            </div>
          ))
        )}
      </div>

      <div className="card">
        <div className="card-hd">
          <div className="flex1">
            <div className="card-hd-title">Recent notifications for {workflowName}</div>
            <div className="card-hd-sub">What was actually delivered, and to whom.</div>
          </div>
        </div>
        {alerts && alerts.length > 0 ? (
          <div>
            {alerts.map((alert) => (
              <AlertFeedRow key={alert.id} alert={alert} channels={channels} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="Nothing sent"
            sub="No rule has fired for this workflow in the retention window."
          />
        )}
      </div>
    </>
  );
}
